/**
 * GEXF export, so a graph can be checked in something other than this app.
 *
 * A finding nobody can reproduce is a claim, not evidence. Gephi reads GEXF
 * natively, and every node and edge attribute drawn on screen goes into the
 * file — including the ones the layout does not show — so the recipient can
 * lay it out differently, filter it, and disagree.
 */

export interface GexfNode {
  id: string;
  label: string;
  attributes?: Record<string, string | number | null | undefined>;
}

export interface GexfEdge {
  source: string;
  target: string;
  label?: string;
  weight?: number;
  attributes?: Record<string, string | number | null | undefined>;
}

/* Attribute values arrive as unknown at this boundary. A primitive
   becomes its text; anything else has no meaningful XML attribute form,
   so it becomes empty rather than the literal "[object Object]". */
function escape(value: unknown): string {
  if (typeof value === 'string') return escapeText(value);
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return escapeText(value.toString());
  }
  // null, undefined, objects, functions and symbols have no attribute form
  return '';
}

function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function declare(keys: string[], forClass: 'node' | 'edge') {
  if (keys.length === 0) return '';
  const rows = keys.map((key, index) =>
    `      <attribute id="${forClass[0]}${index}" title="${escape(key)}" type="string"/>`);
  return `    <attributes class="${forClass}">\n${rows.join('\n')}\n    </attributes>\n`;
}

function values(attributes: Record<string, unknown> | undefined,
                keys: string[], prefix: string) {
  if (!attributes) return '';
  const rows = keys
    .map((key, index) => [key, index] as const)
    .filter(([key]) => attributes[key] != null && attributes[key] !== '')
    .map(([key, index]) =>
      `        <attvalue for="${prefix}${index}" value="${escape(attributes[key])}"/>`);
  return rows.length
    ? `\n      <attvalues>\n${rows.join('\n')}\n      </attvalues>\n    `
    : '';
}

export function toGexf(nodes: GexfNode[], edges: GexfEdge[], description: string): string {
  const nodeKeys = [...new Set(nodes.flatMap((n) => Object.keys(n.attributes ?? {})))];
  const edgeKeys = [...new Set(edges.flatMap((e) => Object.keys(e.attributes ?? {})))];

  const nodeXml = nodes.map((node) =>
    `    <node id="${escape(node.id)}" label="${escape(node.label)}">`
    + `${values(node.attributes, nodeKeys, 'n')}</node>`).join('\n');
  const edgeXml = edges.map((edge, index) =>
    `    <edge id="e${index}" source="${escape(edge.source)}" target="${escape(edge.target)}"`
    + (edge.label ? ` label="${escape(edge.label)}"` : '')
    + (edge.weight != null ? ` weight="${edge.weight}"` : '')
    + `>${values(edge.attributes, edgeKeys, 'e')}</edge>`).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<gexf xmlns="http://www.gexf.net/1.3" version="1.3">
  <meta>
    <creator>Алфа-Омега — от бюджета до получателя</creator>
    <description>${escape(description)}</description>
  </meta>
  <graph mode="static" defaultedgetype="directed">
${declare(nodeKeys, 'node')}${declare(edgeKeys, 'edge')}    <nodes>
${nodeXml}
    </nodes>
    <edges>
${edgeXml}
    </edges>
  </graph>
</gexf>
`;
}

export function downloadGexf(filename: string, nodes: GexfNode[], edges: GexfEdge[],
                             description: string) {
  const blob = new Blob([toGexf(nodes, edges, description)],
    { type: 'application/gexf+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
